import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import ClientsAdminPanel from "../components/ClientsAdminPanel";
import { toast } from "sonner";
import { UserCheck, UserMinus, Check, X, Buildings } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

export default function Approvals() {
    const { user } = useAuth();
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (tab === "clients") { setRows([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/users", { params: { status: tab } });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const approve = async (id, role) => {
        try {
            await api.post(`/users/${id}/approve`, role ? { role } : {});
            toast.success("Approved");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const reject = async (id) => {
        if (!window.confirm("Reject this registration?")) return;
        try {
            await api.post(`/users/${id}/reject`);
            toast.success("Rejected");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const changeRole = async (id, role) => {
        try {
            await api.patch(`/users/${id}/role`, { role });
            toast.success("Role updated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const tabs = [
        { key: "pending", label: "Pending", icon: <UserCheck size={14} /> },
        { key: "approved", label: "Approved", icon: <Check size={14} /> },
        { key: "rejected", label: "Rejected", icon: <UserMinus size={14} /> },
        { key: "clients", label: "Client Logins", icon: <Buildings size={14} /> },
    ];

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-[1200px] mx-auto px-5 py-8">
                <div className="mb-6">
                    <div className="uppercase-label text-[var(--grindrod-red)]">Access control</div>
                    <h1 className="font-display font-black text-3xl text-[var(--grindrod-navy)]">Registrations</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Approve or reject new accounts. Managers can approve supervisors. Admins can grant manager role.
                    </p>
                </div>

                <div className="flex gap-1 mb-4 border-b border-slate-200">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition ${
                                tab === t.key ? "border-[var(--grindrod-red)] text-[var(--grindrod-navy)]" : "border-transparent text-slate-500 hover:text-[var(--grindrod-navy)]"
                            }`}
                            data-testid={`approvals-tab-${t.key}`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                {tab === "clients" ? (
                    <ClientsAdminPanel actorRole={user?.role} />
                ) : (
                <div className="g-card overflow-x-auto">
                    <table className="g-table" data-testid="approvals-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Position</th>
                                <th>Company #</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Requested</th>
                                <th className="text-right pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-slate-400 py-8" data-testid="approvals-empty">No {tab} registrations.</td></tr>
                            ) : rows.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-semibold text-[var(--grindrod-navy)]">{u.name}</td>
                                    <td>{u.position}</td>
                                    <td className="tabular">{u.company_number}</td>
                                    <td>
                                        <span className={`g-pill ${u.role === "admin" ? "g-pill-red" : u.role === "manager" ? "g-pill-navy" : "g-pill-green"}`}>
                                            {u.role}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`g-pill ${u.status === "approved" ? "g-pill-green" : u.status === "pending" ? "g-pill-amber" : "g-pill-red"}`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="text-xs text-slate-500">{new Date(u.created_at).toLocaleString(undefined, { hour12: false })}</td>
                                    <td className="text-right pr-4">
                                        {tab === "pending" && (
                                            <div className="inline-flex gap-2 flex-wrap justify-end">
                                                {user.role === "admin" && (
                                                    <button
                                                        onClick={() => approve(u.id, "manager")}
                                                        className="btn-outline !py-1 !px-2 text-xs"
                                                        data-testid={`approve-as-manager-${u.id}`}
                                                    >
                                                        As Manager
                                                    </button>
                                                )}
                                                {["safety", "client_liaison", "finance", "fel_operator", "fleet_manager", "plant_manager", "shift_millwright", "electrician"].includes(u.role) ? (
                                                    <button
                                                        onClick={() => approve(u.id, u.role)}
                                                        className="btn-navy !py-1 !px-2 text-xs"
                                                        data-testid={`approve-as-${u.role}-${u.id}`}
                                                    >
                                                        <Check size={12} className="inline" /> As {u.role.replace("_", " ")}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => approve(u.id, "supervisor")}
                                                        className="btn-navy !py-1 !px-2 text-xs"
                                                        data-testid={`approve-as-supervisor-${u.id}`}
                                                    >
                                                        <Check size={12} className="inline" /> Supervisor
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => reject(u.id)}
                                                    className="btn-primary !py-1 !px-2 text-xs"
                                                    data-testid={`reject-${u.id}`}
                                                >
                                                    <X size={12} className="inline" /> Reject
                                                </button>
                                            </div>
                                        )}
                                        {tab === "approved" && user.role === "admin" && u.role !== "admin" && (
                                            <select
                                                className="g-input !py-1 !px-2 text-xs inline-block w-auto"
                                                value={u.role}
                                                onChange={(e) => changeRole(u.id, e.target.value)}
                                                data-testid={`role-select-${u.id}`}
                                            >
                                                <option value="supervisor">Supervisor</option>
                                                <option value="manager">Manager</option>
                                                <option value="safety">Safety</option>
                                                <option value="client_liaison">Client Liaison</option>
                                                <option value="finance">Finance</option>
                                                <option value="fel_operator">FEL Operator</option>
                                                <option value="fleet_manager">Fleet Manager</option>
                                                <option value="plant_manager">Plant Manager (view-only)</option>
                                                <option value="shift_millwright">Shift Millwright</option>
                                                <option value="electrician">Electrician</option>
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                )}
            </main>
        </div>
    );
}
